import { DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Col, Menu, MenuProps, Row } from "antd";
import { Avatar, Dropdown, Image } from "components";
import dayjs from "dayjs";
import es from "dayjs/locale/es";
import I18n from "i18n-js";
import { Fragment } from "react";
import { setCurrentBanner, setCurrentCategory, setCurrentCountry, setCurrentProduct, setCurrentZone } from "store/catalogs";
import { setCurrentEvent, setCurrentHunter } from "store/hunters";
import { maskString } from "./formatter";
import { getBooleanText, getCustomBadge } from "./getComponents";
import { setCurrentService } from "store/service";

dayjs.locale(es);

const textLeft = "left";
const textRight = "right";
const textCenter = "center";
const textAscend = "ascend";

type MenuItem = Required<MenuProps>['items'][number];

export const getItem = (
  label: React.ReactNode,
  key: React.Key,
  onClick?: () => void,
  icon?: React.ReactNode,
  children?: MenuItem[],
): MenuItem => {
  return {
    key,
    onClick,
    label,
    icon,
    children
  } as MenuItem;
}

export const dayServicesColumns = (navigate:any, dispatch:any, canEdit = true) => {
    const MenuOptions = (row: any) => {
        return (
            <Menu items={[getItem("Editar", 1, () => {
                dispatch(setCurrentService(row));
                navigate(`/services/detail/${row.id}`);
            })]} />
        );
    };
    return ([
        {
            title: "Folio",
            dataIndex: "id",
            key: "id",
            fixed: textLeft as "left",
            sorter: (a:any, b:any) => a.id - b.id,
            defaultSortOrder: textAscend as 'ascend',
        },
        {
            title: "Nombre",
            dataIndex: "customer",
            key: "customer",
            sorter: (a:any, b:any) => a.customer.localeCompare(b.customer)
        },
        {
            title: "Ciudad",
            dataIndex: "state",
            key: "state",
            sorter: (a:any, b:any) => {
                const stateA = a.state || '';
                const stateB = b.state || '';
                return stateA.localeCompare(stateB)
            }
        },
        {
            title: "Teléfono",
            dataIndex: "phone",
            key: "phone",
            width: "auto",
            render: (text: any) => {
                const phone = text ? text.replace(/[()]/g, "") : "";
                return (
                    <p className="m-0">{maskString(phone ? phone.replace(/ /g,"") : "", "## #### ####")}</p>
                );
            }
        },
        {
            title: "Inicia",
            dataIndex: "date",
            key: "date",
            render: (text:any) => <p className="m-0 text-uppercase">{dayjs(text).format('DD / MMM / YYYY')}</p>,
            sorter: (a:any, b:any) => {
                const date1 = new Date(a.date);
                const date2 = new Date(b.date);
                return date2.valueOf() - date1.valueOf();
            },
        },
        {
            title: "Hora",
            dataIndex: "date",
            key: "date",
            render: (text:any) => <p className="m-0">{dayjs(text).format('hh:mm A')}</p>
        },
        {
            title: "Tipo Pago",
            dataIndex: "payment_type",
            key: "payment_type",
            render: (text:any, record:any) => <Fragment>{text === "cash" ? record.payment_engine === 'Manual' ? 'Transfer' : "Efectivo" : "Tarjeta"}</Fragment>,
            sorter: (a:any, b:any) => a.payment_type.localeCompare(b.payment_type),
        },
        {
            title: "Costo",
            dataIndex: "price",
            key: "price",
            render: (text:any, record: any) => record.currency === 'EUR' ? `€${text ? Number(text.toString()).toFixed(2)  : '0.00'}` : I18n.l('currency', text || 0),
            sorter: (a:any, b:any) => b.price - a.price,

        },
        {
            title: "Viáticos",
            dataIndex: "travelers",
            key: "travelers",
            render: (text:any, record:any) => record.currency === 'EUR' ? <p style={{textAlign: 'right', margin: 0 }} >{`€${text ? Number(text.toString()).toFixed(2)  : '0.00'}`}</p> : <p style={{textAlign: 'right', margin: 0 }} >{ I18n.l('currency', text || 0)}</p>,
            sorter: (a:any, b:any) => b.price - a.price,

        },
        {
            title: "Moneda",
            dataIndex: "currency",
            key: "currency",
            render: (text:any) => <p style={{ margin: 0, textAlign: 'center' }}>{text}</p>,
            sorter: (a:any, b:any) => { return  a.currency.toLowerCase() > b.currency.toLowerCase() ? 1 : -1 } ,

        },
        {
            title: "Canal",
            dataIndex: "channel",
            key: "channel",
            render: (text:any) => <p style={{textTransform: 'capitalize'}} >{text.replaceAll("_", "")}</p>
        },
        {
            title: "Estatus",
            dataIndex: "status",
            key: "status",
            render: (text:any) => getCustomBadge(text, true),
            sorter: (a:any, b:any) => getBooleanText(a.status).localeCompare(getBooleanText(b.status))
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown overlay={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        }
    ]);
}

export const eventColumns = (dispatch:any, showModal:any, canEdit = true) => {
    const MenuOptions = (row: any) => {
        return (
            <Menu items={[getItem("Editar", 1, () => {
                dispatch(setCurrentEvent(row));
                showModal();
            })]} />
        );
    };

    return ([
        {
            title: "Código",
            dataIndex: "code",
            key: "code",
            fixed: textLeft as "left"
        },
        {
            title: "Descripción",
            dataIndex: "description",
            key: "description"
        },
        {
            title: "Descuento",
            dataIndex: "discount",
            key: "discount",
            render: (text:any) => `${text} %`,
            sorter: (a:any, b:any) => b.discount - a.discount
        },
        {
            title: "Inicia",
            dataIndex: "starts_at",
            key: "starts_at",
            render: (text:any) => <p className="m-0 text-uppercase">{dayjs(text).format('DD / MMM / YYYY')}</p>,
            sorter: (a:any, b:any) => {
                const date1 = new Date(a.starts_at)
                const date2 = new Date(b.starts_at)
                return date2.valueOf() - date1.valueOf();
            },
            defaultSortOrder: textAscend as 'ascend',
        },
        {
            title: "Expira",
            dataIndex: "expires_at",
            key: "expires_at",
            render: (text:any) => <p className="m-0 text-uppercase">{dayjs(text).format('DD / MMM / YYYY')}</p>,
            sorter: (a:any, b:any) => {
                const date1 = new Date(a.expires_at)
                const date2 = new Date(b.expires_at)
                return date2.valueOf() - date1.valueOf();
            },
        },
        {
            title: "Estatus",
            dataIndex: "status",
            key: "status",
            render: (text:any) => getCustomBadge(text ? "active" : "inactive", true),
            sorter: (a:any, b:any) => getBooleanText(a.status).localeCompare(getBooleanText(b.status)),
        },
        {
            title: "Hunter",
            dataIndex: "hunter",
            key: "hunter",
            render: (text:any) => {
                return <Fragment>{text ? `${text.name}${text.lastnames ? ` ${text.lastnames}`  : ""}` : "Null"}</Fragment>
            }
        },
        {
            title: "Uso",
            dataIndex: "uses",
            key: "uses",
            fixed: textRight as "right"
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown overlay={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        }
    ]);
}

export const hunterColumns = (dispatch:any, showModal:any, canEdit = true) => {
    const MenuOptions = (record:any) => {
        return (
            <Menu items={[getItem("Editar", 1, () => {
                dispatch(setCurrentHunter(record));
                showModal();
            })]} />
        );
    }

    return ([
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
            fixed: textLeft as "left",
            width: "250px",
            sorter: (a:any, b:any) => {
                const nameA = `${a.name || ''} ${a.lastnames}`;
                const nameB = `${b.name || ''} ${b.lastnames}`;
                return nameA.localeCompare(nameB)
            },
            render: (text: any, row: any) => {
                return <p className="m-0 text-capitalize">{`${text} ${row.lastnames}`}</p>
            }
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            width: "auto",
            render: (text: any) => {
                return <p className="m-0">{`${text ? text.toLowerCase() : ''}`}</p>
            }
        },
        {
            title: "Teléfono",
            dataIndex: "phone",
            key: "phone",
            fixed: textRight as "right",
            width: "calc(20%)",
            render: (text: any) => {
                const phone = text ? text.replace(/[()]/g, "") : "";
                return <p className="m-0 text-capitalize">{maskString(phone ? phone.replace(/ /g,"") : "", "## #### ####")}</p>
            }
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown overlay={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        },
    ]);
}

export const walletColumns = (dispatch:any, showModal:any, setCurrentWallet:any, canEdit = true) => {
    const MenuOptions = (record:any) => {
        return (
            <Menu items={[getItem("Editar", 1, () => {
                // dispatch(setCurrentWallet(record));
                setCurrentWallet(record);
                showModal();
            })]} />
        );
    }

    return ([
        {
            title: "Código",
            dataIndex: "code",
            key: "code",
            fixed: textLeft as "left",
            width: "100px",
            sorter: (a:any, b:any) => {
                const nameA = `${a.code || ''}`;
                const nameB = `${b.code || ''}`;
                return nameA.localeCompare(nameB)
            },
            render: (text: any) => {
                return <p className="m-0 text-capitalize">{`${text}`}</p>
            }
        },
        {
            title: "Tipo",
            dataIndex: "type",
            key: "type",
            width: "100px",
            render: (text: any) => {
                return <p className="m-0">{`${text === 'Amount' ? "Monto" : 'Producto'}`}</p>
            }
        },
        {
            title: "Expiración",
            dataIndex: "expires_at",
            key: "expires_at",
            fixed: textRight as "right",
            width: "100px",
            render: (createdAt: any) => {
                const date = dayjs(createdAt).format('DD MMM YY-HH:mm').toString().replaceAll(" ", "/").replaceAll("-"," - ");
                return (
                    <p style={{textTransform: 'capitalize', textAlign: 'start', margin:"0"}} >{date}</p>
                )
            },
            sorter: (a:any, b:any) => {
                const date1 = new Date(a.expires_at);
                const date2 = new Date(b.expires_at);
                return date1.valueOf() - date2.valueOf();
            }
        },
        {
            title: "Estatus",
            dataIndex: "status",
            key: "status",
            width: "80px",
            render: (text:any) => getCustomBadge(text.toString(), true),
            sorter: (a:any, b:any) => getBooleanText(a.status).localeCompare(getBooleanText(b.status))
        },
        {
            title: "Relación de uso",
            dataIndex: "status",
            key: "status",
            width: "80px",
            render: (createdAt: any,record: any) => {
                return (
                    <p style={{textTransform: 'capitalize', textAlign: 'start', margin:"0"}} >{`${record.use_count}/${record.remaining_uses}`}</p>
                )
            },
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown overlay={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        },
    ]);
}

export const goodlookeroColumns = (onEdit: (row:any) => void, onGenerateQr: (row:any) => void, canEdit = true) => {
    const MenuOptions = (row: any) => {
        return (
            <Menu items={[
                getItem("Editar", 1, () => onEdit(row)),
                getItem("Generar QR", 2, () => onGenerateQr(row))
            ]} />
        );
    };

    return ([
        {
            title: "Foto",
            dataIndex: "Foto",
            key: "Foto",
            width: "80px",
            render: (foto: any) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Image 
                            image={foto} 
                            preview={true}
                            style={{ 
                                width: 50, 
                                height: 50, 
                                borderRadius: '50%', 
                                objectFit: 'cover' 
                            }} 
                        />
                    </div>
                )
            }
        },
        {
            title: "Nombre",
            dataIndex: "Nombre",
            key: "Nombre",
            fixed: textLeft as "left",
            width: "360px",
            sorter: (a:any, b:any) => {
                const nameA = `${a.Nombre || ''}`;
                const nameB = `${b.Nombre || ''}`;
                return nameA.localeCompare(nameB)
            },
            render: (text: any) => {
                return (
                    <p className="m-0 text-capitalize" style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {`${text}`}
                    </p>
                )
            }
        },
        {
            title: "Estatus",
            dataIndex: "Activo",
            key: "Activo",
            width: "100px",
            render: (text:any) => getCustomBadge(text.toString(), true),
            sorter: (a:any, b:any) => getBooleanText(a.Activo).localeCompare(getBooleanText(b.Activo))
        },
        {
            title: "Categoría",
            dataIndex: "Categoria",
            key: "Categoria",
            width: "120px",
            render: (text: any) => {
                return <p className="m-0">{`${text || '-'}`}</p>
            },
            sorter: (a:any, b:any) => {
                const catA = `${a.Categoria || ''}`;
                const catB = `${b.Categoria || ''}`;
                return catA.localeCompare(catB)
            }
        },
        {
            title: "Ciudades",
            dataIndex: "Ciudades",
            key: "Ciudades",
            render: (cities: any) => {
                if (!Array.isArray(cities) || cities.length === 0) return <p className="m-0">-</p>;
                
                const displayCities = cities.slice(0, 2);
                const hasMore = cities.length > 2;
                
                return (
                    <div>
                        <p className="m-0" style={{ marginBottom: '4px' }}>
                            {displayCities.join(", ")}
                        </p>
                        {hasMore && (
                            <span style={{ 
                                fontSize: '12px', 
                                color: '#1890ff',
                                cursor: 'pointer',
                                fontWeight: 500
                            }}>
                                +{cities.length - 2} más
                            </span>
                        )}
                    </div>
                )
            }
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown overlay={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        },
    ]);
}

export const countryColumns = (dispatch:any, showModal:any, canEdit = true) => {
    const MenuOptions = (row:any) => {
        return (
            <Menu items={[getItem("Editar", 1, () => {
                dispatch(setCurrentCountry(row));
                showModal();
            })]} />
        );
    }

    return ([
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            fixed: textLeft as "left"
        },
        {
            title: "País",
            dataIndex: "name",
            key: "name"
        },
        {
            title: "Factor",
            dataIndex: "factor",
            key: "factor"
        },
        {
            title: "Moneda",
            dataIndex: "currency",
            key: "currency",
            fixed: textRight as "right"
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown overlay={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        }
    ]);
}

export const goodLooksColumns = (navigate:any, canEdit = true) => {
    const MenuOptions = (row: any) => {
        return (
            <Menu items={[getItem("Editar", 1, () => {
                navigate(`/goodlooks/${row.id}`);
            })]} />
        );
    };
    return ([
        {
            title: "Folio",
            dataIndex: "id",
            key: "id",
            fixed: textLeft as "left",
        },
        {
            title: "Avatar",
            dataIndex: "photo_uri",
            key: "photo_uri",
            fixed: textLeft as "left",
            align: textCenter as "center",
            render:(text:any, row:any) => text ? 
                <Avatar margin={"auto"} size={"small"} icon={<Image style={{ borderRadius: "50%" }} preview={false} image={text} alt="gl-logo" />} />
                :
                <Avatar margin={"auto"} size={"small"} nameStyle={{ fontSize: 12 }} backgroundColor="#FC4B6C" name={`${row.name} ${row.lastnames || ""}`} />
        },
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
            render: (text:any, row:any) => <Fragment>{`${text} ${row.lastnames}`}</Fragment>
        },
        {
            title: "Teléfono",
            dataIndex: "phone",
            key: "phone"
        },
        {
            title: "Correo Electrónico",
            dataIndex: "email",
            key: "email"
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown overlay={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        }
    ]);
}

export const categoryColumns = (dispatch:any, showModal:any, setCurrentOrder: any, canEdit = true) => {
    const MenuOptions = (row: any) => {
        return (
            <Menu items={[getItem("Editar", 1, () => {
                dispatch(setCurrentCategory(row));
                setCurrentOrder(row.order);
                showModal();
            })]} />
        );
    };

    return ([
        {
            title: "Orden",
            dataIndex: "order",
            key: "order",
            fixed: textLeft as "left",
            sorter: (a:any, b:any) => a.id - b.id,
        },
        {
            title: "Categoría",
            dataIndex: "name",
            key: "name",
            sorter: (a:any, b:any) => a.name.es.localeCompare(b.name.es),
            render: (text:any) => text ? text.es : ""
        },
        {
            title: "Descripción",
            dataIndex: "description",
            key: "description",
            render: (text:any) => text ? text.es : ""
        },
        {
            title: "Estatus",
            dataIndex: "status",
            key: "status",
            render: (text:any) => getCustomBadge(text ? "active" : "inactive", true),
            sorter: (a:any, b:any) => getBooleanText(a.status).localeCompare(getBooleanText(b.status)),
            defaultSortOrder: textAscend as 'ascend',
        },
        {
            title: "Cover",
            dataIndex: "cover",
            key: "cover",
            align: textCenter as "center",
            render: (text:any) => <Image image={text} preview={false} style={{ height: 100 }}/>
        },
        {
            title: "Imagen",
            dataIndex: "photo",
            key: "photo",
            fixed: textRight as "right",
            align: textCenter as "center",
            render: (text:any) => <Image image={text} preview={false} style={{ width: 100 }}/>
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown overlay={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        }
    ]);
}

export const productColumns = (dispatch:any, showModal:any, canEdit = true) => {

    const MenuOptions = (row: any) => {
        const items: MenuProps['items'] = [
            {
                key: '1',
                label: 'Editar',
                onClick: ()=>{
                    dispatch(setCurrentProduct(row));
                    showModal();
                }
            },
        ];
        return {items} ;
    };

    return ([
        {
            title: "Order",
            dataIndex: "order",
            key: "order",
            fixed: textLeft as "left",
            sorter: (a:any, b:any) => a.order - b.order,
        },
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
            sorter: (a:any, b:any) => a.name.es.localeCompare(b.name.es),
            render: (text:any) => text ? text.es : ""
        },
        {
            title: "Descripción",
            dataIndex: "description",
            key: "description",
            render: (text:any) => text ? text.es : ""
        },
        {
            title: "MEX",
            dataIndex: "sell_price",
            key: "sell_price",
            render: (text:any) => I18n.l('currency', text || 0),
            sorter: (a:any, b:any) => a.sell_price - b.sell_price,
        },
        {
            title: "US",
            dataIndex: "sell_price_usd",
            key: "sell_price_usd",
            render: (text:any) => I18n.l('currency', text || 0),
            sorter: (a:any, b:any) => a.sell_price_usd - b.sell_price_usd,
        },
        {
            title: "ESP",
            dataIndex: "sell_price_eur",
            key: "sell_price_eur",
            render: (text:any) =>  `€${text ? Number(text.toString()).toFixed(2) : '0.00'}`,
            sorter: (a:any, b:any) => a.sell_price_eur - b.sell_price_eur,
        },
        {
            title: "ARG",
            dataIndex: "sell_price_arg",
            key: "sell_price_arg",
            render: (text:any) =>  `$${text ? Number(text.toString()).toFixed(2) : '0.00'}`,
            sorter: (a:any, b:any) => a.sell_price_arg - b.sell_price_arg,
        },

        {
            title: "CHL",
            dataIndex: "sell_price_clp",
            key: "sell_price_clp",
            render: (text:any) =>  `$${text ? Number(text.toString()).toFixed(2) : '0.00'}`,
            sorter: (a:any, b:any) => a.sell_price_clp - b.sell_price_clp,
        },
        {
            title: "PER",
            dataIndex: "sell_price_pen",
            key: "sell_price_pen",
            render: (text:any) =>  `$${text ? Number(text.toString()).toFixed(2) : '0.00'}`,
            sorter: (a:any, b:any) => a.sell_price_pen - b.sell_price_pen,
        },
        {
            title: "PRI",
            dataIndex: "sell_price_pr",
            key: "sell_price_pr",
            render: (text:any) =>  `$${text ? Number(text.toString()).toFixed(2) : '0.00'}`,
            sorter: (a:any, b:any) => a.sell_price_pr - b.sell_price_pr,
        },
        {
            title: "COL",
            dataIndex: "sell_price_co",
            key: "sell_price_co",
            render: (text:any) =>  `$${text ? Number(text.toString()).toFixed(2) : '0.00'}`,
            sorter: (a:any, b:any) => a.sell_price_co - b.sell_price_co,
        },
        {
            title: "PAN",
            dataIndex: "sell_price_pa",
            key: "sell_price_pa",
            render: (text:any) =>  `$${text ? Number(text.toString()).toFixed(2) : '0.00'}`,
            sorter: (a:any, b:any) => a.sell_price_pa - b.sell_price_pa,
        },
        {
            title: "FRA",
            dataIndex: "sell_price_fra",
            key: "sell_price_fra",
            render: (text:any) =>  `€${text ? Number(text.toString()).toFixed(2) : '0.00'}`,
            sorter: (a:any, b:any) => a.sell_price_fra - b.sell_price_fra,
        },
        {
            title: "ITA",
            dataIndex: "sell_price_ita",
            key: "sell_price_ita",
            render: (text:any) =>  `€${text ? Number(text.toString()).toFixed(2) : '0.00'}`,
            sorter: (a:any, b:any) => a.sell_price_ita - b.sell_price_ita,
        },




        {
            title: "Estatus",
            dataIndex: "status",
            key: "status",
            render: (text:any) => getCustomBadge(text ? "active" : "inactive", true),
            sorter: (a:any, b:any) => getBooleanText(a.status).localeCompare(getBooleanText(b.status)),
            defaultSortOrder: textAscend as 'ascend',
        },
        {
            title: "Imagen",
            dataIndex: "photo",
            key: "photo",
            fixed: textRight as "right",
            align: textCenter as "center",
            render: (text:any) => <Image image={text} preview={false} style={{ width: 250 }}/>
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown menu={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        }
    ]);
}

export const cartColumns = (currency:string, updateCount: any, removeItem:any) => {


    return ([
        {
            title: "Imagen",
            dataIndex: "photo",
            key: "photo",
            fixed: textRight as "right",
            align: textCenter as "center",
            render: (text:any) => <Image className="avatar-icon" image={text} preview={false}  />
        },
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
            align: textCenter as "center",
            fixed: textRight as "right",
            sorter: (a:any, b:any) => a.name.es.localeCompare(b.name.es),
            render: (text:any) => text ? text.es : ""
        },
        {
            title: currency,
            dataIndex: `${currency === 'MXN' ? "sell_price" : "sell_price_usd" }` ,
            key: `${currency === 'MXN' ? "sell_price" : "sell_price_usd" }`,
            align: textCenter as "center",
            fixed: textRight as "right",
            render: (text:any, record:any) => I18n.l('currency', text * record.count || 0),
            sorter: (a:any, b:any) =>  currency === 'MXN' ? a.sell_price - b.sell_price : a.sell_price_usd - b.sell_price_usd   ,
        },
        {
            title: "Cantidad",
            dataIndex: "count",
            key: "count",
            width: "12em",
            fixed: textRight as "right",
            align: textCenter as "center",
            render: (text:any, record:any) => {
                return (
                    <Row style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}} >
                        <Col style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '85%'}}>
                            <Button className="btnCart" disabled={record.count === 1} onClick={()=> updateCount(record.order, 'subtract')} >-</Button>
                                {text}
                            <Button className="btnCart" onClick={()=> updateCount(record.order, 'add')} >+</Button>
                        </Col>
                        <Col style={{width: '10%', cursor: 'pointer'}} >
                            <DeleteOutlined onClick={()=>removeItem(record.order)} className="deleteIcon" />
                        </Col>
                    </Row>
                )
            },
        },
    ]);
}

export const bannerColumns = (dispatch:any, showModal:any, canEdit = true) => {
    const MenuOptions = (row: any) => {
        return (
            <Menu items={[getItem("Editar", 1, () => {
                dispatch(setCurrentBanner(row));
                showModal();
            })]} />
        );
    };

    return ([
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            fixed: textLeft as "left",
            sorter: (a:any, b:any) => a.id - b.id,
        },
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
            sorter: (a:any, b:any) => a.name.localeCompare(b.name)
        },
        {
            title: "Descripción",
            dataIndex: "description",
            key: "description"
        },
        {
            title: "Link",
            dataIndex: "url",
            key: "url"
        },
        {
            title: "Imagen",
            dataIndex: "photo",
            key: "photo",
            fixed: textRight as "right",
            align: textCenter as "center",
            render: (text:any) => <Image image={text} preview={false} style={{ width: 250 }}/>
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown overlay={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        }
    ]);
}

export const zonesColumns = (dispatch:any, navigate:any, canEdit = true) => {
    const MenuOptions = (row: any) => {
        return (
            <Menu items={[getItem("Editar", 1, () => {
                dispatch(setCurrentZone(row));
                navigate(`/zones/${row.id}`);
            })]} />
        );
    };

    return ([
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            fixed: textLeft as "left",
            sorter: (a:any, b:any) => a.id - b.id,
        },
        {
            title: "Estado",
            dataIndex: "administrative_area_level_1",
            key: "administrative_area_level_1",
            render: (text:any) => text || "",
            sorter: (a:any, b:any) => (a.administrative_area_level_1 || "").localeCompare(b.administrative_area_level_1 || "")
        },
        {
            title: "Localidad",
            dataIndex: "locality",
            key: "locality"
        },
        {
            title: "Factor",
            dataIndex: "factor",
            key: "factor",
            fixed: textRight as "right",
            // align: textCenter as "center"
        },
        {
            title: "Acciones",
            key: "action",
            align: textCenter as "center",
            fixed: textRight as "right",
            width: "6em",
            render: (record: any) => {
                if (!canEdit) return null;
                return (
                    <Dropdown overlay={MenuOptions(record)} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            },
        }
    ]);
}
